/**
 * @fileoverview API endpoint definitions and configuration
 * Centralized API endpoint definitions with type safety
 */

// Base API configuration
const resolveBaseUrl = (): string => {
  const envBase = import.meta.env?.VITE_API_URL as string | undefined;
  if (envBase && typeof envBase === 'string' && envBase.trim().length > 0) {
    return envBase.replace(/\/$/, '');
  }
  return '/api';
};

export const API_CONFIG = {
  BASE_URL: resolveBaseUrl(),
  TIMEOUT: 300000, // 5 minutes (300 seconds) - increased for long-running backtests
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
} as const;

// API endpoint definitions
export const API_ENDPOINTS = {
  // Authentication endpoints
  AUTH: {
    LOGIN: '/auth/login',
    SIGNUP: '/auth/signup',
    REFRESH: '/auth/refresh',
    LOGOUT: '/auth/logout',
    PROFILE: '/auth/profile',
  },
  
  // Backtest endpoints
  BACKTESTS: {
    LIST: '/backtests',
    CREATE: '/backtests',
    PREVIEW: '/backtests/preview',
    SAVE: '/backtests/save',
    GET: (id: string) => `/backtests/${id}`,
    DELETE: (id: string) => `/backtests/${id}`,
    BULK_DELETE: '/backtests/bulk-delete',
    UPDATE: (id: string) => `/backtests/${id}`,
  },
  
  // Optimization endpoints
  OPTIMIZATION: {
    START: '/optimize/strategy',
    PROGRESS: (id: string) => `/optimize/progress/${id}`,
    RESULTS: (id: string) => `/optimize/results/${id}`,
    ITERATIONS: '/optimize/iteration-count',
    HISTORICAL: {
      LIST: '/optimize/jobs/historical',
      GET: (id: string) => `/optimize/jobs/historical/${id}`,
      ARCHIVE: (id: string) => `/optimize/jobs/historical/${id}`,
      BULK_ARCHIVE: '/optimize/jobs/historical/bulk'
    },
  },
  
  // Trading data endpoints
  TRADING: {
    PAIRS: '/trading-pairs',
    STRATEGIES: '/strategies/backtest',
    INTERVALS: '/intervals',
    EARLIEST_DATE: (symbol: string, interval: string) => `/earliest-date/${symbol}/${interval}`,
    STATS: '/trades/stats',
  },

  // Strategy endpoints
  STRATEGIES: {
    LIST: '/strategies',
    BACKTEST: '/strategies/backtest',
    MARKETPLACE: '/strategies/marketplace',
    CREATE: '/strategies',
    GET: (id: string) => `/strategies/${id}`,
    UPDATE: (id: string) => `/strategies/${id}`,
    DELETE: (id: string) => `/strategies/${id}`,
    SHARE: (id: string) => `/strategies/${id}/share`,
    RATE: (id: string) => `/strategies/${id}/rate`,
    USER_FEATURES: '/strategies/user/features',
    TEMPLATES: '/strategies/templates',
    CLONE: (id: string) => `/strategies/clone/${id}`,
    CLONED: '/strategies/cloned',
  },
  
  // Signal data endpoints
  SIGNALS: {
    GET: (backtestId: string) => `/signals/${backtestId}`,
    LIST: '/signals',
  },
  
  // Billing endpoints
  BILLING: {
    CREATE_CHECKOUT: '/billing/create-checkout-session',
    WEBHOOK: '/billing/webhook',
    ESTIMATE_COST: '/billing/estimate-cost',
  },

  // System endpoints (public, no auth required)
  SYSTEM: {
    SETTINGS: '/admin/system/settings',
  },
} as const;

// HTTP Methods
export const HTTP_METHODS = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
} as const;

export type HttpMethod = typeof HTTP_METHODS[keyof typeof HTTP_METHODS];

// API Response wrapper type
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  metadata?: {
    timestamp: string;
    version: string;
    requestId: string;
  };
}

// Request configuration
export interface RequestConfig extends Omit<RequestInit, 'method' | 'body'> {
  timeout?: number;
  retry?: boolean;
  retryAttempts?: number;
  retryDelay?: number;
}

// Response types for different endpoints
// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace ApiTypes {
  // Auth types
  export interface LoginRequest {
    email: string;
    password: string;
    twoFactorToken?: string;
  }
  
  export interface LoginResponse {
    token?: string;
    requiresTwoFactor?: boolean;
    message?: string;
    user?: {
      email: string;
      role: 'trial' | 'user' | 'admin';
      subscriptionStatus: string;
      tier: 'standard' | 'independent' | 'professional' | 'advanced';
      subscriptionPlan?: string;
      canShareStrategies: boolean;
      maxCustomStrategies: number;
      strategiesCreated: number;
      savedBacktests: number;
    };
  }
  
  export interface SignupRequest {
    email: string;
    password: string;
    name?: string;
    agreedToTerms?: boolean;
  }
  
  // Backtest types
  export interface BacktestPreviewRequest {
    symbol: string;
    interval: string;
    strategy: string;
    portfolioAmount: number;
    portfolioCurrency: string;
    portfolioType?: 'USD' | 'ASSET' | 'MIXED';
    params: Record<string, unknown>;
    tradingMode?: 'LONG_ONLY' | 'SHORT_ONLY' | 'BIDIRECTIONAL';
    shortSellingMode?: 'REINVEST_PROFITS' | 'FIXED_AMOUNT' | 'CAPPED_AMOUNT';
    capPercent?: number;
    startDate?: string;
    endDate?: string;
    startCandle?: number;
    endCandle?: number;
    feePercent?: number; // Custom trading fee percentage (0-5%)
    // Exit management parameters
    stopLossPercent?: number;
    takeProfitPercent?: number;
    trailingStopPercent?: number;
    slippagePercent?: number;
    candles?: Array<{
      timestamp: Date | string;
      open: number;
      high: number;
      low: number;
      close: number;
      volume: number;
    }>; // Optional candles array for custom/synthetic data
  }
  
  export interface BacktestSaveRequest {
    name: string;
    symbol: string;
    interval: string;
    strategy: string;
    portfolioAmount: number;
    portfolioCurrency: string;
    params: Record<string, unknown>;
    tradingMode?: 'LONG_ONLY' | 'SHORT_ONLY' | 'BIDIRECTIONAL';
    isShared?: boolean;
    source?: 'manual' | 'optimization' | 'wfa' | 'research-lab'; // Track origin of backtest
    signals: Array<{
      timestamp: string;
      price: number;
      type: string;
      strength?: number;
    }>;
    trades: Array<{
      _id: string;
      symbol: string;
      tradeType: string;
      avgEntry: number;
      avgExit: number;
      openedAt: string;
      closedAt: string;
      portfolioAfterTrade: number;
      totalProfit: number;
    }>;
    stats: {
      totalReturn: number;
      totalReturnPercent?: number;
      sharpeRatio: number;
      winRate: number;
      maxDrawdown: number;
      maxDrawdownPercent: number;
      tradeCount: number;
      totalTrades?: number;
      profitFactor?: number;
      averageWin?: number;
      averageLoss?: number;
      wins?: number;
      losses?: number;
      portfolioCurrency?: string;
      enhanced?: Record<string, unknown>;
      drawdown?: Record<string, unknown>;
      regimes?: Record<string, unknown>;
      [key: string]: unknown; // Allow additional stats fields
    };
  }
  
  // Optimization types
  export interface OptimizationRequest {
    symbol: string;
    interval: string;
    strategy: string;
    portfolioAmount: number;
    portfolioCurrency: string;
    parameterRanges: Record<string, {
      min: number;
      max: number;
      step: number;
    }>;
    optimizationTarget: string;
    tradingMode?: 'LONG_ONLY' | 'SHORT_ONLY' | 'BIDIRECTIONAL';
    // Configuration parameters that affect backtest results
    shortSellingMode?: 'REINVEST_PROFITS' | 'CAPPED_AMOUNT';
    capPercent?: number;
    feePercent?: number;
    portfolioType?: 'USD' | 'ASSET' | 'MIXED';
    limit?: number;
    startDate?: string;
    endDate?: string;
    startCandle?: number;  // Candle range support
    endCandle?: number;    // Candle range support
    enableProgressUpdates?: boolean;
  }
  
  export interface OptimizationResponse {
    optimizationId: string;
    status: 'started' | 'running' | 'completed' | 'failed';
    estimatedDuration?: number;
  }
  
  // Trading data types
  export interface Strategy {
    id: string;
    name: string;
    type: 'built-in' | 'custom' | 'shared';
    supportedTradingModes?: string[];
  }
  
  export interface TradingOptions {
    tradingPairs: string[];
    strategies: Strategy[];
    intervals: string[];
  }
  
  export interface StrategyStats {
    totalTrades: number;
    winRate: number;
    avgReturn: number;
    maxDrawdown: number;
    sharpeRatio: number;
  }
}
